import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useTheme, useSystem, API } from "../App";
import { Button } from "./ui/button";
import NotificationBell from "./NotificationBell";
import WhatsAppNotifications from "./WhatsAppNotifications";
import EncuestasAlertasNotifications from "./EncuestasAlertasNotifications";
import axios from "axios";
import * as LucideIcons from "lucide-react";
import {
  LogOut,
  Bug,
  ShieldAlert,
  User,
  ChevronDown,
  ChevronRight,
  Moon,
  Sun,
  Menu,
  X,
  Loader2,
  Key,
  BookOpen
} from "lucide-react";

const DEFAULT_LOGO_URL = "/logo-qasesores.png";

// Icon mapper - converts string icon name to Lucide component
const getIcon = (iconName) => {
  if (!iconName) return LucideIcons.Circle;
  const Icon = LucideIcons[iconName];
  return Icon || LucideIcons.Circle;
};

export default function Sidebar() {
  const { user, logout, sidebarMenus, sidebarMenusLoading, getAuthHeaders } = useAuth();
  const [vistaInfo, setVistaInfo] = useState(null); // Ver como: { permitido, roles, vista_activa, vista_rol }
  const [cambiandoVista, setCambiandoVista] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/vista-rol/opciones`, getAuthHeaders())
      .then((r) => setVistaInfo(r.data))
      .catch(() => setVistaInfo(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const cambiarVista = async (rol) => {
    try {
      setCambiandoVista(true);
      await axios.post(`${API}/vista-rol`, { rol: rol || null }, getAuthHeaders());
      window.location.reload();
    } catch {
      setCambiandoVista(false);
    }
  };
  const { darkMode, toggleDarkMode } = useTheme();
  const { systemInfo } = useSystem();
  const location = useLocation();
  const navigate = useNavigate();
  
  const menus = sidebarMenus || [];
  const loadingMenus = sidebarMenusLoading;
  const [openMenus, setOpenMenus] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const hasAccess = useCallback((permissionKey) => {
    const userRole = user?.role?.toLowerCase();
    if (userRole === 'admin' || userRole === 'administrador') return true;
    if (!user?.menu_permissions) return false;
    const permission = user.menu_permissions[permissionKey];
    if (permission?.access === false) return false;
    if (permission?.access === true) return true;
    return false;
  }, [user]);

  const isPathActive = useCallback((path) => {
    if (path === "/") return location.pathname === "/";
    // Exact match only — evita que un padre (ej. /pipelines) se marque como activo
    // cuando estamos en un submenu más específico (/pipelines/informes).
    return location.pathname === path;
  }, [location.pathname]);

  const toggleMenu = (menuId) => {
    setOpenMenus(prev => {
      if (prev[menuId]) return { ...prev, [menuId]: false };
      return { [menuId]: true };
    });
  };

  useEffect(() => {
    if (menus.length > 0 && Object.keys(openMenus).length === 0) {
      const activeMenu = menus.find(menu => 
        menu.submenus?.some(sub => isPathActive(sub.path))
      );
      if (activeMenu) setOpenMenus({ [activeMenu.id]: true });
    }
  }, [menus]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!user) return null;
  
  // Mensajeros use the kiosk directly, no sidebar needed
  if (user.role?.toLowerCase() === "mensajero") return null;

  const logoUrl = systemInfo?.logo_url || DEFAULT_LOGO_URL;

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="px-3 py-2 border-b border-slate-300 bg-transparent flex items-center justify-between">
        <Link to="/" className="flex items-center justify-center flex-1" data-testid="sidebar-logo-link">
          <img
            src={logoUrl}
            alt={systemInfo?.name || "Sistema"}
            className="w-full max-w-[180px] h-auto"
            data-testid="sidebar-logo-img"
            onError={(e) => {
              // Si el logo configurado en systemInfo falla (URL rota/CORS), caemos al DEFAULT.
              // Si DEFAULT también falla, mostramos el nombre del sistema como texto.
              const fallback = DEFAULT_LOGO_URL;
              if (e.currentTarget.src !== fallback) {
                e.currentTarget.src = fallback;
              } else {
                e.currentTarget.style.display = "none";
                const parent = e.currentTarget.parentElement;
                if (parent && !parent.querySelector("[data-logo-text-fallback]")) {
                  const span = document.createElement("span");
                  span.setAttribute("data-logo-text-fallback", "1");
                  span.className = "text-slate-800 font-bold text-lg";
                  span.textContent = systemInfo?.name || "QAsesores";
                  parent.appendChild(span);
                }
              }
            }}
          />
        </Link>
        <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 text-slate-600" onClick={() => setMobileOpen(false)}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* User Info */}
      <div className="px-3 py-2 border-b border-slate-300">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-800 truncate">{user.name || user.email}</p>
            <p className="text-[10px] text-slate-600 truncate">{user.departamento_nombre || user.department || "Sin departamento"}</p>
          </div>
        </div>
        {/* Ver como: ver la plataforma con otro rol (admin o usuarios autorizados) */}
        {vistaInfo?.permitido && (
          <div className="mt-2">
            {vistaInfo.vista_activa ? (
              <div className="rounded-md bg-amber-50 border border-amber-300 px-2 py-1.5" data-testid="vista-activa-banner">
                <p className="text-[10px] font-semibold text-amber-700 truncate">
                  Viendo como: {(vistaInfo.roles || []).find((r) => r.id === vistaInfo.vista_rol)?.name || vistaInfo.vista_rol}
                </p>
                <button
                  onClick={() => cambiarVista(null)}
                  disabled={cambiandoVista}
                  className="text-[10px] font-medium text-amber-700 underline hover:text-amber-900 disabled:opacity-50"
                  data-testid="vista-volver-btn"
                >
                  Volver a mi vista real
                </button>
              </div>
            ) : (
              <select
                value=""
                disabled={cambiandoVista}
                onChange={(e) => e.target.value && cambiarVista(e.target.value)}
                className="w-full h-7 text-[11px] rounded-md bg-white border border-slate-300 text-slate-600 px-1.5 cursor-pointer"
                data-testid="vista-rol-select"
                title="Ver la plataforma con otro rol (para pruebas)"
              >
                <option value="">👁 Ver como…</option>
                {(vistaInfo.roles || []).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-1">
        {loadingMenus ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <ul className="space-y-0.5">
            {menus.map((menu) => {
              const hasDirectPath = menu.path && (!menu.submenus || menu.submenus.length === 0);
              const accessibleSubmenus = menu.submenus?.filter(sub => {
                if (user?.menu_permissions?.[sub.id]?.access === false) return false;
                const permissionKey = sub.permission || sub.id;
                return hasAccess(sub.id) || hasAccess(permissionKey);
              }) || [];
              
              if (!hasDirectPath && accessibleSubmenus.length === 0 && !hasAccess(menu.id)) return null;

              const MenuIcon = getIcon(menu.icon);
              const isOpen = openMenus[menu.id];
              const hasActiveSubmenu = accessibleSubmenus.some(sub => isPathActive(sub.path));
              const isDirectPathActive = hasDirectPath && isPathActive(menu.path);

              if (hasDirectPath) {
                if (!hasAccess(menu.id) && !hasAccess(menu.permission || menu.id)) return null;
                return (
                  <li key={menu.id}>
                    <Link
                      to={menu.path}
                      className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        isDirectPathActive
                          ? "bg-[#193966] text-white shadow-sm"
                          : "text-[#193966] hover:bg-[#80BC00]/15 hover:text-[#5f8f00]"
                      }`}
                      data-testid={`nav-${menu.id}`}
                    >
                      <MenuIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{menu.label}</span>
                    </Link>
                  </li>
                );
              }

              return (
                <li key={menu.id}>
                  <button
                    onClick={() => toggleMenu(menu.id)}
                    className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      isOpen || hasActiveSubmenu
                        ? "bg-[#193966] text-white shadow-sm"
                        : "text-[#193966] hover:bg-[#80BC00]/15 hover:text-[#5f8f00]"
                    }`}
                    data-testid={`nav-${menu.id}`}
                  >
                    <MenuIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{menu.label}</span>
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                  {isOpen && accessibleSubmenus.length > 0 && (
                    <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-[#80BC00]/40 pl-2">
                      {accessibleSubmenus.map((submenu) => {
                        const SubIcon = getIcon(submenu.icon);
                        const isSubActive = isPathActive(submenu.path);
                        
                        // Check for nested submenus
                        const nestedSubmenus = submenu.submenus?.filter(nested => {
                          if (user?.menu_permissions?.[nested.id]?.access === false) return false;
                          return hasAccess(nested.id) || hasAccess(nested.permission || nested.id);
                        }) || [];

                        if (nestedSubmenus.length > 0) {
                          const isNestedOpen = openMenus[submenu.id];
                          return (
                            <li key={submenu.id}>
                              <button
                                onClick={() => toggleMenu(submenu.id)}
                                className={`w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                                  isNestedOpen ? "bg-[#193966] text-white" : "text-[#5f8f00] hover:bg-[#193966]/10 hover:text-[#193966]"
                                }`}
                              >
                                <SubIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="flex-1 text-left truncate">{submenu.label}</span>
                                {isNestedOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              </button>
                              {isNestedOpen && (
                                <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-[#193966]/20 pl-2">
                                  {nestedSubmenus.map((nested) => {
                                    const NestedIcon = getIcon(nested.icon);
                                    const isNestedActive = isPathActive(nested.path);
                                    return (
                                      <li key={nested.id}>
                                        <Link
                                          to={nested.path}
                                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${
                                            isNestedActive ? "bg-[#193966] text-white" : "text-[#5f8f00] hover:bg-[#193966]/10 hover:text-[#193966]"
                                          }`}
                                        >
                                          <NestedIcon className="w-3 h-3 flex-shrink-0" />
                                          <span className="truncate">{nested.label}</span>
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          );
                        }

                        return (
                          <li key={submenu.id}>
                            <Link
                              to={submenu.path}
                              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                                isSubActive
                                  ? "bg-gradient-to-r from-[#193966] to-[#2a5490] text-white shadow-sm border-l-2 border-[#80BC00]"
                                  : "text-[#5f8f00] hover:bg-[#193966]/10 hover:text-[#193966]"
                              }`}
                              data-testid={`nav-${submenu.id}`}
                            >
                              <SubIcon className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="flex-1 truncate">{submenu.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Notifications */}
      <div className="px-3 py-2 border-t border-slate-300 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <NotificationBell />
          <WhatsAppNotifications />
          <EncuestasAlertasNotifications />
        </div>
        <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="text-slate-600 hover:text-slate-900 h-8 w-8">
          {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
      </div>

      {/* User Actions */}
      <div className="px-3 py-2 border-t border-slate-300 space-y-1">
        <Button variant="ghost" onClick={() => navigate("/manual")} className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-400/50 h-8 text-xs" data-testid="manual-btn">
          <BookOpen className="w-4 h-4 mr-1.5" />
          Manual de Usuario
        </Button>
        <Button variant="ghost" onClick={() => navigate("/cambiar-contrasena")} className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-400/50 h-8 text-xs" data-testid="change-password-btn">
          <Key className="w-4 h-4 mr-1.5" />
          Cambiar Contraseña
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            try {
              if (location?.pathname && location.pathname !== "/reportar-error") {
                sessionStorage.setItem("report_referrer_path", location.pathname);
              }
            } catch (_) { /* sessionStorage puede no estar disponible */ }
            navigate("/reportar-error");
          }}
          className="w-full justify-start text-rose-700 hover:text-rose-900 hover:bg-gradient-to-r hover:from-rose-200/80 hover:to-orange-200/60 h-8 text-xs font-semibold relative group"
          data-testid="reportar-error-btn"
        >
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r bg-gradient-to-b from-rose-500 to-orange-500" />
          <Bug className="w-4 h-4 mr-1.5 text-rose-600 group-hover:animate-pulse" />
          Reportar un problema
        </Button>
        {(user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador" || user?.role?.toLowerCase() === "superadmin") && (
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/reportes-errores")}
            className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-300/60 h-8 text-xs font-medium"
            data-testid="admin-reportes-errores-btn"
          >
            <ShieldAlert className="w-4 h-4 mr-1.5 text-slate-600" />
            Gestionar Reportes (Admin)
          </Button>
        )}
        <Button variant="ghost" onClick={handleLogout} className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-400/50 h-8 text-xs" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-1.5" />
          Cerrar Sesión
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger button - fixed top-left */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-40 md:hidden bg-white dark:bg-slate-800 shadow-lg rounded-lg p-2 border border-slate-200 dark:border-slate-700"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5 text-slate-700 dark:text-slate-300" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar - hidden on mobile, visible on md+ */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 border-r border-slate-300 flex flex-col z-50 transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        style={{ backgroundColor: '#D6D4D4' }}
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
}
